import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ReadOnlyField } from '~components/FormFields/ReadOnlyField';
import { SwitchFormField } from '~components/FormFields/SwitchFormField';
import TextInput from '~components/FormFields/v2/TextInput';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import useFormSubmitHandle, { FormSubmitHandle } from '~formsShared/useFormSubmitHandle';
import { HaulWareConnection } from '~hooks/useHaulWare/models';

import {
  HaulWareConnectionDto,
  haulWareConnectionSchema,
  setHaulWareConnectionSchemaDefaultValues,
} from './haulWareConnectionSchema';

const EXPORT_AFTER_DISPLAY_FORMAT = 'MMM D, YYYY h:mm A';

// The API never returns the password, so the mask is fixed rather than derived.
const PASSWORD_MASK = '••••';

export type HaulWareConnectionFormHandler = FormSubmitHandle<HaulWareConnectionDto>;

interface HaulWareConnectionFormProps {
  haulWareConnection: HaulWareConnection;
  isDisabled: boolean;
}

const HaulWareConnectionForm = forwardRef<
  HaulWareConnectionFormHandler,
  HaulWareConnectionFormProps
>(function HaulWareConnectionForm({ haulWareConnection, isDisabled }, ref) {
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const form = useForm<HaulWareConnectionDto>({
    resolver: yupResolver(haulWareConnectionSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setHaulWareConnectionSchemaDefaultValues(haulWareConnection),
  });
  const {
    control,
    formState: { errors },
    resetField,
  } = form;

  useFormSubmitHandle(ref, form);

  const handleCancelPasswordChange = () => {
    resetField('apiPassword');
    setIsChangingPassword(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '560px' }}>
      <SwitchFormField
        control={control}
        disabled={isDisabled}
        errors={errors}
        label={t('administration.integration.haul_ware.form.enabled_label')}
        name="enabled"
        value={haulWareConnection.enabled}
      />

      <TextInput
        control={control}
        dataUi="haul-ware-app-code"
        isDisabled={isDisabled}
        isRequired
        label={t('administration.integration.haul_ware.form.app_code_label')}
        name="appCode"
      />

      <ReadOnlyField
        label={t('administration.integration.haul_ware.form.api_user_label')}
        value={haulWareConnection.apiUser}
      />

      {isChangingPassword ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextInput
            control={control}
            dataUi="haul-ware-api-password"
            slotProps={{ htmlInput: { type: 'password' } }}
            isDisabled={isDisabled}
            label={t('administration.integration.haul_ware.form.api_password_label')}
            name="apiPassword"
          />
          <Box sx={{ display: 'flex' }}>
            <SmallButton
              color="brandV2Black"
              disabled={isDisabled}
              onClick={handleCancelPasswordChange}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
          <ReadOnlyField
            label={t('administration.integration.haul_ware.form.api_password_label')}
            value={PASSWORD_MASK}
          />
          <SmallButton
            color="brandV2Black"
            disabled={isDisabled}
            onClick={() => setIsChangingPassword(true)}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
            variant="outlined"
          >
            {t('administration.integration.haul_ware.cta.change_password')}
          </SmallButton>
        </Box>
      )}

      <ReadOnlyField
        label={t('administration.integration.haul_ware.form.export_after_label')}
        value={
          haulWareConnection.exportAfter
            ? dayjs.tz(haulWareConnection.exportAfter).format(EXPORT_AFTER_DISPLAY_FORMAT)
            : ''
        }
      />
    </Box>
  );
});

export default HaulWareConnectionForm;
